import type { Schemas } from '@ers/sdk';
import type { Tone } from '../../components/ui/Badge';

/*
 * Людські назви значень управлінського контуру (як у Управлінському порталі) і тони стану.
 * Колір — лише для стану: «Є ризик» і межі — бурштиновий, «Заблоковано», прострочення й «поза графіком» — червоний,
 * «Завершено» й «Прийнято» — зелений; решта статусів — нейтральні.
 */

export type MgmtUnit = Schemas['MgmtUnit'];
export type MgmtUnitDetails = Schemas['MgmtUnitDetails'];
export type UnitStatus = MgmtUnit['status'];
export type UnitHealth = MgmtUnit['health'];
export type UnitKind = MgmtUnit['kind'];
export type UnitPriority = MgmtUnit['priority'];
export type AttentionReason = MgmtUnitDetails['attention'][number];

export const UNIT_STATUS: Record<UnitStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Чернетка', tone: 'neutral' },
  idea: { label: 'Ідея', tone: 'neutral' },
  planned: { label: 'Заплановано', tone: 'neutral' },
  ready: { label: 'Готово до старту', tone: 'neutral' },
  in_progress: { label: 'У роботі', tone: 'neutral' },
  in_acceptance: { label: 'На прийманні', tone: 'info' },
  done: { label: 'Завершено', tone: 'success' },
  paused: { label: 'Призупинено', tone: 'neutral' },
  cancelled: { label: 'Скасовано', tone: 'neutral' },
};

export const UNIT_STATUS_ORDER: UnitStatus[] = ['draft', 'idea', 'planned', 'ready', 'in_progress', 'in_acceptance', 'done', 'paused', 'cancelled'];

export const UNIT_HEALTH: Record<UnitHealth, { label: string; tone: Tone }> = {
  ok: { label: 'Нормально', tone: 'neutral' },
  at_risk: { label: 'Є ризик', tone: 'warning' },
  blocked: { label: 'Заблоковано', tone: 'danger' },
};

/** Типові назви рівнів (організація може перейменувати — див. levelNames у GET /mgmt/me чи /mgmt/tree). */
export const UNIT_KIND: Record<UnitKind, string> = {
  goal: 'Стратегічна ціль',
  direction: 'Напрям зусиль',
  project: 'Проєкт',
  task: 'Завдання',
};

export const UNIT_PRIORITY: Record<UnitPriority, { label: string; tone: Tone }> = {
  critical: { label: 'Критичний', tone: 'warning' },
  high: { label: 'Високий', tone: 'neutral' },
  normal: { label: 'Нормальний', tone: 'neutral' },
  low: { label: 'Низький', tone: 'neutral' },
};

/** Причини уваги (одиниці й задачі області дії). */
export const ATTENTION: Record<string, { label: string; tone: Tone }> = {
  blocked: { label: 'Заблоковано', tone: 'danger' },
  at_risk: { label: 'Є ризик', tone: 'warning' },
  unanswered_question: { label: 'Питання без відповіді', tone: 'info' },
  unanswered_signal: { label: 'Сигнал без відповіді', tone: 'warning' },
  unanswered_comment: { label: 'Коментар без відповіді', tone: 'info' },
  decision_pending: { label: 'Рішення не прийнято', tone: 'info' },
  acceptance_pending: { label: 'Результат не прийнято', tone: 'info' },
  done_without_acceptance: { label: 'Завершено без приймання', tone: 'warning' },
  overdue: { label: 'Прострочено', tone: 'danger' },
  no_report: { label: 'Немає звіту', tone: 'warning' },
  no_active_tasks: { label: 'Немає активних завдань', tone: 'warning' },
  stale: { label: 'Без руху', tone: 'warning' },
  watch_due: { label: 'Настала дата контролю', tone: 'info' },
  report_requested: { label: 'Запитано звіт', tone: 'info' },
};

export function attentionLabel(r: string): { label: string; tone: Tone } {
  return ATTENTION[r] ?? { label: r, tone: 'neutral' };
}

export const TRACK_STATUS: Record<'on_track' | 'at_risk' | 'off_track', { label: string; tone: Tone }> = {
  on_track: { label: 'У графіку', tone: 'neutral' },
  at_risk: { label: 'З ризиком', tone: 'warning' },
  off_track: { label: 'Поза графіком', tone: 'danger' },
};

export const GOAL_STATUS: Record<NonNullable<Schemas['MgmtGoal']['status']>, { label: string; tone: Tone }> = {
  on_track: { label: 'У графіку', tone: 'neutral' },
  at_risk: { label: 'З ризиком', tone: 'warning' },
  off_track: { label: 'Поза графіком', tone: 'danger' },
  achieved: { label: 'Досягнуто', tone: 'success' },
  partial: { label: 'Досягнуто частково', tone: 'warning' },
  missed: { label: 'Не досягнуто', tone: 'danger' },
  dropped: { label: 'Знято', tone: 'neutral' },
};

export const PROGRESS_METHOD: Record<MgmtUnit['progressMethod'], string> = {
  auto: 'З дочірніх одиниць і звітів',
  metric: 'З показника',
  manual: 'Вручну',
};

export const GOAL_PROGRESS_SOURCE: Record<Schemas['MgmtGoal']['progressSource'], string> = {
  manual: 'Вручну',
  subgoals: 'З підцілей',
  links: 'З пов’язаних проєктів і одиниць',
  metric: 'З показника',
};

export const METRIC_PERIOD: Record<Schemas['MgmtMetric']['period'], string> = {
  week: 'Тиждень',
  month: 'Місяць',
  quarter: 'Квартал',
  year: 'Рік',
  once: 'Одноразово',
};

export const METRIC_SOURCE: Record<Schemas['MgmtMetric']['source'], string> = {
  manual: 'Вручну',
  finance: 'Фінансові дані',
  time: 'Облік часу',
  tasks: 'Задачі',
  legal: 'Юридичний контур',
  external: 'Зовнішнє джерело',
};

export const CONTROL_PLACE_KIND: Record<Schemas['MgmtControlPlace']['kind'], string> = {
  task: 'Задача',
  project: 'Проєкт',
  matter: 'Справа',
  drive: 'Google Drive',
  kommo: 'Угода CRM',
  url: 'Посилання',
};

export const MESSAGE_KIND: Record<Schemas['MgmtMessage']['kind'], string> = {
  comment: 'Коментар',
  question: 'Питання',
  answer: 'Відповідь',
  signal: 'Сигнал про перешкоду',
  system: 'Службовий запис',
};

export const BLOCKER_APPROVAL: Record<Schemas['MgmtBlocker']['approval'], { label: string; tone: Tone }> = {
  pending: { label: 'Очікує реакції', tone: 'info' },
  approved: { label: 'Погоджую', tone: 'neutral' },
  rejected: { label: 'Відмовлено', tone: 'neutral' },
};

export const BLOCKER_STATUS: Record<Schemas['MgmtBlocker']['status'], { label: string; tone: Tone }> = {
  open: { label: 'Відкритий', tone: 'danger' },
  resolved: { label: 'Усунено', tone: 'success' },
  rejected: { label: 'Відхилено', tone: 'neutral' },
};

export const DECISION_STATUS: Record<Schemas['MgmtDecision']['status'], { label: string; tone: Tone }> = {
  requested: { label: 'Очікує рішення', tone: 'info' },
  decided: { label: 'Ухвалено', tone: 'success' },
  cancelled: { label: 'Відкликано', tone: 'neutral' },
};

export const ACCEPTANCE_STATUS: Record<Schemas['MgmtAcceptance']['status'], { label: string; tone: Tone }> = {
  pending: { label: 'На прийманні', tone: 'info' },
  accepted: { label: 'Прийнято', tone: 'success' },
  returned: { label: 'Повернуто', tone: 'warning' },
  withdrawn: { label: 'Відкликано', tone: 'neutral' },
};

export const AGREEMENT_STATUS: Record<Schemas['MgmtAgreement']['status'], { label: string; tone: Tone }> = {
  open: { label: 'Очікує запуску', tone: 'neutral' },
  launched: { label: 'Запущено', tone: 'success' },
  not_launched: { label: 'Не запущено', tone: 'danger' },
  done: { label: 'Виконано', tone: 'success' },
  cancelled: { label: 'Скасовано', tone: 'neutral' },
};

export const ESCALATION_STATUS: Record<Schemas['MgmtEscalation']['status'], { label: string; tone: Tone }> = {
  open: { label: 'Відкрита', tone: 'danger' },
  acknowledged: { label: 'Отримання підтверджено', tone: 'warning' },
  closed: { label: 'Закрита', tone: 'neutral' },
};

export const ESCALATION_TRIGGER: Record<Schemas['MgmtEscalation']['trigger'], string> = {
  legal_deadline_risk: 'Ризик пропуску юридичного строку',
  data_leak: 'Витік або надмірний доступ до конфіденційних даних',
  wrong_payment: 'Неправильний платіж',
  unverified_legal_result: 'Юридичний результат без належної перевірки',
  repeat_critical_defect: 'Повторний критичний дефект',
  public_complaint: 'Публічна скарга',
  rule_conflict: 'Конфлікт правил із законом чи фактичною системою',
};

export const CIRCLE_ROLE: Record<Schemas['MgmtCircleMember']['role'], string> = {
  manager: 'Керівник',
  coordinator: 'Координатор',
  observer: 'Спостерігач',
};

export const CIRCLE_SCOPE: Record<Schemas['MgmtCircleMember']['scopeType'], string> = {
  org: 'Уся організація',
  department: 'Підрозділ з дочірніми',
  subordinates: 'Підлеглі',
  goal_branch: 'Гілка цілей',
};

export const CALENDAR_EVENT: Record<Schemas['MgmtCalendarEvent']['type'], { label: string; tone: 'neutral' | 'danger' | 'warning' | 'success' | 'info' }> = {
  start: { label: 'Початок', tone: 'neutral' },
  due: { label: 'Строк', tone: 'neutral' },
  forecast: { label: 'Прогноз', tone: 'warning' },
  coordination: { label: 'Координація', tone: 'info' },
  blocker_reaction: { label: 'Реакція на блокер', tone: 'danger' },
  decision_due: { label: 'Строк рішення', tone: 'info' },
};

export const DEPENDENCY_TYPE: Record<'FS' | 'SS' | 'FF' | 'SF', string> = {
  FS: 'Після завершення',
  SS: 'Після початку',
  FF: 'Завершення після завершення',
  SF: 'Завершення після початку',
};

export const DUE_SHIFT_SOURCE: Record<Schemas['MgmtDueShift']['source'], string> = {
  report: 'Звіт',
  passport: 'Паспорт',
  forecast: 'Прогноз',
  rollup: 'Зведення з дочірніх',
  task: 'Картка задачі',
};
